unit UWebReportUtils;

interface

uses
  JS, System.Contnrs, System.Classes, WebLib.ExtCtrls, UUserModel, UFakModel;

type
  TReportItemKind = (ikUnknown, ikHeading, ikParagraph, ikImage, ikArtList,
    ikSign, ikComment);

  TListChangedEvent = procedure(Sender: TObject) of object;
  TPropertyChangedEvent = procedure(Sender: TObject; PropertyName: string)
    of object;
  TListErrorEvent = procedure(Sender: TObject; AMessage: string) of object;
  TFileSaveEvent = procedure(Sender: TObject) of object;
  TFileOpenEvent = procedure(Sender: TObject; ABase64: string) of object;
  THtmlReadyEvent = procedure(Sender: TObject; AHtml: string) of object;

  TReportArtItem = class;
  TReportArtArray = array of TReportArtItem;

  TReportBase = class
  private
    FOnPropertyChanged: TPropertyChangedEvent;
    procedure ClearValues; virtual;
    procedure DoPropertyChanged(PropertyName: string);
    procedure SetValues(Json: string); virtual;
    procedure SetOnPropertyChanged(const Value: TPropertyChangedEvent);
  public
    constructor Create; overload;
    constructor Create(Json: string); overload;
    function GetContent: string; virtual;
    function GetHtml: string; virtual;
    function ToJson: string; virtual;
    property OnPropertyChanged: TPropertyChangedEvent read FOnPropertyChanged
      write SetOnPropertyChanged;
  end;

  TReportHeading = class(TReportBase)
  private
    FAlign: string;
    FSize: string;
    FContent: string;
    procedure ClearValues; override;
    procedure SetValues(Json: string); override;
    procedure SetAlign(const Value: string);
    procedure SetContent(const Value: string);
    procedure SetSize(const Value: string);
  public
    function GetContent: string; override;
    function GetHtml: string; override;
    function ToJson: string; override;
    property Size: string read FSize write SetSize;
    property Align: string read FAlign write SetAlign;
    property Content: string read FContent write SetContent;
  end;

  TReportParagraph = class(TReportBase)
  private
    FContent: TStringList;
    procedure ClearValues; override;
    procedure HandleChanged(Sender: TObject);
    procedure SetValues(Json: string); override;
    procedure SetContent(const Value: TStringList);
  public
    function GetContent: string; override;
    function GetHtml: string; override;
    function ToJson: string; override;
    property Content: TStringList read FContent write SetContent;
  end;

  TReportImage = class(TReportBase)
  private
    FLegend: string;
    FFilename: string;
    FOnFileSave: TFileSaveEvent;
    FOnFileOpen: TFileOpenEvent;
    FCol: Integer;
    FRow: Integer;
    FPathname: string;
    procedure ClearValues; override;
    procedure DoFileOpen(ABase64: string);
    procedure DoFileSave;
    procedure HandleFileSave(Sender: TObject);
    procedure SetValues(Json: string); override;
    procedure SetLegend(const Value: string);
    procedure SetFilename(const Value: string);
    procedure SetOnFileSave(const Value: TFileSaveEvent);
    procedure SetOnFileOpen(const Value: TFileOpenEvent);
    procedure SetCol(const Value: Integer);
    procedure SetRow(const Value: Integer);
    procedure SetPathname(const Value: string);
  public
    constructor Create; overload;
    constructor Create(Json: string); overload;
    function FormatBase64(Value: string; Small: Boolean): string;
    function ToJson: string; override;
    procedure OpenFile;
    procedure SaveFile(ABase64: string);
    property Legend: string read FLegend write SetLegend;
    property Filename: string read FFilename write SetFilename;
    property Pathname: string read FPathname write SetPathname;
    property OnFileSave: TFileSaveEvent read FOnFileSave write SetOnFileSave;
    property OnFileOpen: TFileOpenEvent read FOnFileOpen write SetOnFileOpen;
    property Row: Integer read FRow write SetRow;
    property Col: Integer read FCol write SetCol;
  end;

  TReportComment = class(TReportBase)
  private
    FFilename: string;
    FPathname: string;
    FOnFileOpen: TFileOpenEvent;
    FCol: Integer;
    FRow: Integer;
    procedure ClearValues; override;
    procedure DoFileOpen(Text: string);
    procedure SetValues(Json: string); override;
    procedure SetFilename(const Value: string);
    procedure SetPathname(const Value: string);
    procedure SetOnFileOpen(const Value: TFileOpenEvent);
    procedure SetCol(const Value: Integer);
    procedure SetRow(const Value: Integer);
  public
    function FormatHtml(Content: string): string;
    function ToJson: string; override;
    procedure OpenFile;
    property Filename: string read FFilename write SetFilename;
    property Pathname: string read FPathname write SetPathname;
    property OnFileOpen: TFileOpenEvent read FOnFileOpen write SetOnFileOpen;
    property Col: Integer read FCol write SetCol;
    property Row: Integer read FRow write SetRow;
  end;

  TReportSign = TReportImage;

  TReportArtItem = class(TReportBase)
  private
    FBezeichnung: string;
    FMenge: Double;
    FArtNr: string;
    FEinheit: string;
    procedure ClearValues; override;
    procedure SetValues(Json: string); override;
    procedure SetArtNr(const Value: string);
    procedure SetBezeichnung(const Value: string);
    procedure SetMenge(const Value: Double);
    procedure SetEinheit(const Value: string);
  public
    function GetHtml: string; override;
    function ToJson: string; override;
    property ArtNr: string read FArtNr write SetArtNr;
    property Menge: Double read FMenge write SetMenge;
    property Bezeichnung: string read FBezeichnung write SetBezeichnung;
    property Einheit: string read FEinheit write SetEinheit;
  end;

  TReportArtList = class(TReportBase)
  private
    FCount: Integer;
    FItems: TReportArtArray;
    procedure DoPropertyChanged(PropertyName: string);
    procedure HandlePropertyChanged(Sender: TObject; PropertyName: string);
    procedure SetValues(Json: string); override;
    procedure SetCount(const Value: Integer);
  public
    constructor Create; overload;
    constructor Create(Json: string); overload;
    function GetContent: string; override;
    function GetHtml: string; override;
    function GetItem(i: Integer): TReportArtItem;
    function ToJson: string; override;
    procedure Add(Item: TReportArtItem);
    procedure Delete(Index: Integer);
    property Count: Integer read FCount write SetCount;
  end;

  TReportItem = class
  private
    FKind: TReportItemKind;
    FBezeichnung: string;
    FOnPropertyChanged: TPropertyChangedEvent;
    FSubItem: TReportBase;
    procedure ClearValues;
    procedure DoPropertyChanged(PropertyName: string);
    procedure HandlePropertyChanged(Sender: TObject; PropertyName: string);
    procedure SetValues(Json: string); overload;
    procedure SetValues(Obj: TJsObject); overload;
    procedure SetKind(const Value: TReportItemKind);
    procedure SetBezeichnung(const Value: string);
    procedure SetOnPropertyChanged(const Value: TPropertyChangedEvent);
    procedure SetSubItem(const Value: TReportBase);
  public
    constructor Create; overload;
    constructor Create(Json: string); overload;
    function Equals(Other: TReportItem): Boolean;
    function ToString: string; override;
    function ToJson: string;
    property Kind: TReportItemKind read FKind write SetKind;
    property Bezeichnung: string read FBezeichnung write SetBezeichnung;
    property OnPropertyChanged: TPropertyChangedEvent read FOnPropertyChanged
      write SetOnPropertyChanged;
    property SubItem: TReportBase read FSubItem write SetSubItem;
  end;

  TReportItemList = class(TObjectList)
  private
    FKey: string;
    FOnListChanged: TListChangedEvent;
    FCreating: Boolean;
    FOnListError: TListErrorEvent;
    FOnHtmlReady: THtmlReadyEvent;
    FTimer: TTimer;
    Fi: Integer;
    FLst: TStringList;
    FSigned: Boolean;
    FOnPropertyChanged: TPropertyChangedEvent;
    FSelectedItem: TReportItem;
    function GetItem(i: Integer): TReportItem;
    function HtmlHeader: string;
    function HtmlFooter: string;
    function ToJson: string;
    procedure DoHtmlReady(AHtml: string);
    procedure DoListChanged;
    procedure DoListError(AMessage: string);
    procedure DoPropertyChanged(PropertyName: string);
    procedure HandlePropertyChanged(Sender: TObject; PropertyName: string);
    procedure TimerOnTime(Sender: TObject);
    procedure SetValues(Json: string); overload;
    procedure SetValues(Obj: TJsObject); overload;
    procedure SetOnListChanged(const Value: TListChangedEvent);
    procedure SetOnListError(const Value: TListErrorEvent);
    procedure SetOnHtmlReady(const Value: THtmlReadyEvent);
    procedure SetOnPropertyChanged(const Value: TPropertyChangedEvent);
    procedure SetSigned(const Value: Boolean);
    procedure SetSelectedItem(const Value: TReportItem);
  public
    constructor Create(Key: string);
    function IndexOf(Item: TReportItem): Integer;
    function IsSigned: Boolean;
    procedure Add(Item: TReportItem);
    procedure CreateHtml;
    procedure Delete(Index: Integer);
    procedure Insert(Index: Integer; Item: TReportItem);
    procedure Move(FromIndex: Integer; ToIndex: Integer);
    procedure Reset;
    property Items[i: Integer]: TReportItem read GetItem;
    property Key: string read FKey;
    property OnListChanged: TListChangedEvent read FOnListChanged
      write SetOnListChanged;
    property OnListError: TListErrorEvent read FOnListError
      write SetOnListError;
    property OnHtmlReady: THtmlReadyEvent read FOnHtmlReady
      write SetOnHtmlReady;
    property Signed: Boolean read FSigned write SetSigned;
    property OnPropertyChanged: TPropertyChangedEvent read FOnPropertyChanged
      write SetOnPropertyChanged;
    property SelectedItem: TReportItem read FSelectedItem
      write SetSelectedItem;
  end;

  TReport = class
  private
    FReportItemList: TReportItemList;
    FName: string;
    FDatum: TDateTime;
    FFak: TFakModel;
    FUser: TUserModel;
    FPdfFilename: string;
    FPdfPathname: string;
    FCreatedAt: TDateTime;
    FTransmittedAt: TDateTime;
    FOnPropertyChanged: TPropertyChangedEvent;
    FPdfFileExists: Boolean;
    procedure CheckPdfFile;
    procedure ClearValues;
    procedure DoPropertyChanged(PropertyName: string);
    procedure HandlePropertyChanged(Sender: TObject; PropertyName: string);
    procedure SetValues(Json: string);
    procedure SetReportItemList(const Value: TReportItemList);
    procedure SetDatum(const Value: TDateTime);
    procedure SetFak(const Value: TFakModel);
    procedure SetName(const Value: string);
    procedure SetUser(const Value: TUserModel);
    procedure SetPdfFilename(const Value: string);
    procedure SetPdfPathname(const Value: string);
    procedure SetCreatedAt(const Value: TDateTime);
    procedure SetTransmittedAt(const Value: TDateTime);
    procedure SetOnPropertyChanged(const Value: TPropertyChangedEvent);
    procedure SetPdfFileExists(const Value: Boolean);
  public
    constructor Create; overload;
    constructor Create(Json: string); overload;
    function Equals(Other: TReport): Boolean;
    function ToJson: string;
    function ToString: string; override;
    property ReportItemList: TReportItemList read FReportItemList
      write SetReportItemList;
    property Name: string read FName write SetName;
    property Datum: TDateTime read FDatum write SetDatum;
    property User: TUserModel read FUser write SetUser;
    property Fak: TFakModel read FFak write SetFak;
    property PdfPathname: string read FPdfPathname write SetPdfPathname;
    property PdfFilename: string read FPdfFilename write SetPdfFilename;
    property CreatedAt: TDateTime read FCreatedAt write SetCreatedAt;
    property TransmittedAt: TDateTime read FTransmittedAt
      write SetTransmittedAt;
    property OnPropertyChanged: TPropertyChangedEvent read FOnPropertyChanged
      write SetOnPropertyChanged;
    property PdfFileExists: Boolean read FPdfFileExists write SetPdfFileExists;
  end;

  TReportList = class(TObjectList)
  private
    FKey: string;
    FOnListChanged: TListChangedEvent;
    FOnListError: TListErrorEvent;
    FFak: TFakModel;
    FSelectedItem: TReport;
    FOnPropertyChanged: TPropertyChangedEvent;
    FPersonalNr: string;
    function GetItem(i: Integer): TReport;
    procedure DoListChanged;
    procedure DoListError(AMsg: string);
    procedure DoPropertyChanged(PropertyName: string);
    procedure HandlePropertyChanged(Sender: TObject; PropertyName: string);
    procedure SetValues(Json: string);
    procedure SetOnListChanged(const Value: TListChangedEvent);
    procedure SetOnListError(const Value: TListErrorEvent);
    procedure SetFak(const Value: TFakModel);
    procedure SetSelectedItem(const Value: TReport);
    procedure SetOnPropertyChanged(const Value: TPropertyChangedEvent);
    procedure SetPersonalNr(const Value: string);
  public
    constructor Create;
    function Exists(Item: TReport): Boolean;
    function GetByPathAndFilename(APathname: string; AFilename: string)
      : TReport;
    function IndexOf(Item: TReport): Integer;
    function ToJson: string;
    procedure Add(Item: TReport);
    procedure Delete(i: Integer);
    procedure Load;
    procedure Store;
    property Items[i: Integer]: TReport read GetItem;
    property OnListChanged: TListChangedEvent read FOnListChanged
      write SetOnListChanged;
    property OnListError: TListErrorEvent read FOnListError
      write SetOnListError;
    property Fak: TFakModel read FFak write SetFak;
    property SelectedItem: TReport read FSelectedItem write SetSelectedItem;
    property OnPropertyChanged: TPropertyChangedEvent read FOnPropertyChanged
      write SetOnPropertyChanged;
    property PersonalNr: string read FPersonalNr write SetPersonalNr;
  end;

function ReportItemKindToStr(Value: TReportItemKind): string;
function StrToReportItemKind(Value: string): TReportItemKind;

implementation

uses
  WebLib.Json, System.SysUtils, WebLib.Storage, WebLib.LocalFiles;

{ TWebReportItem }

procedure TReportItem.ClearValues;
begin
  Self.Kind := ikUnknown;
  Self.Bezeichnung := '';
end;

constructor TReportItem.Create;
begin
  Self.OnPropertyChanged := nil;
  Self.ClearValues;
end;

constructor TReportItem.Create(Json: string);
begin
  Self.OnPropertyChanged := nil;
  Self.SetValues(Json);
end;

procedure TReportItem.DoPropertyChanged(PropertyName: string);
begin
  if Assigned(Self.OnPropertyChanged) then
    Self.OnPropertyChanged(Self, PropertyName);
end;

function TReportItem.Equals(Other: TReportItem): Boolean;
begin
  if Other <> nil then
    Result := (Self.Kind = Other.Kind) and
      (Self.Bezeichnung = Other.Bezeichnung)
  else
    Result := false;
end;

procedure TReportItem.HandlePropertyChanged(Sender: TObject;
  PropertyName: string);
begin
  Self.DoPropertyChanged(PropertyName);
end;

procedure TReportItem.SetBezeichnung(const Value: string);
begin
  if FBezeichnung <> Value then
  begin
    FBezeichnung := Value;
    Self.DoPropertyChanged('Bezeichnung');
  end;
end;

procedure TReportItem.SetKind(const Value: TReportItemKind);
begin
  if FKind <> Value then
  begin
    FKind := Value;
    Self.DoPropertyChanged('Kind');
  end;
end;

procedure TReportItem.SetOnPropertyChanged(const Value
  : TPropertyChangedEvent);
begin
  FOnPropertyChanged := Value;
end;

procedure TReportItem.SetSubItem(const Value: TReportBase);
begin
  FSubItem := Value;
  if Self.SubItem <> nil then
    Self.SubItem.OnPropertyChanged := Self.HandlePropertyChanged;
end;

procedure TReportItem.SetValues(Obj: TJsObject);
begin
  Self.ClearValues;
  if Obj['Kind'] <> nil then
    Self.Kind := StrToReportItemKind(string(Obj['Kind']));
  if Obj['Bezeichnung'] <> nil then
    Self.Bezeichnung := string(Obj['Bezeichnung']);
  if Obj['SubItem'] <> nil then
  begin
    case Self.Kind of
      ikUnknown:
        ;
      ikHeading:
        Self.SubItem := TReportHeading.Create(string(Obj['SubItem']));
      ikParagraph:
        Self.SubItem := TReportParagraph.Create(string(Obj['SubItem']));
      ikImage:
        Self.SubItem := TReportImage.Create(string(Obj['SubItem']));
      ikArtList:
        Self.SubItem := TReportArtList.Create(string(Obj['SubItem']));
      ikSign:
        Self.SubItem := TReportSign.Create(string(Obj['SubItem']));
      ikComment:
        Self.SubItem := TReportComment.Create(string(Obj['SubItem']));
    end;
  end;
end;

procedure TReportItem.SetValues(Json: string);
var
  Obj: TJsObject;
begin
  Obj := TJsJson.parseObject(Json);
  Self.SetValues(Obj);
end;

function TReportItem.ToJson: string;
var
  Obj: TJsonObject;
begin
  Obj := TJsonObject.Create;
  Obj.AddPair('Kind', ReportItemKindToStr(Self.Kind));
  Obj.AddPair('Bezeichnung', Self.Bezeichnung);
  if Self.SubItem <> nil then
    Obj.AddPair('SubItem', Self.SubItem.ToJson);
  Result := Obj.ToJson;
  Obj.Free;
end;

function TReportItem.ToString: string;
begin
  Result := 'Art: ' + ReportItemKindToStr(Self.Kind) + #13#10;
  Result := Result + 'Bez: ' + Self.Bezeichnung;
end;

function ReportItemKindToStr(Value: TReportItemKind): string;
begin
  case Value of
    ikUnknown:
      Result := 'unbekannt';
    ikHeading:
      Result := 'Überschrift';
    ikParagraph:
      Result := 'Absatz';
    ikImage:
      Result := 'Bild';
    ikArtList:
      Result := 'Artikelliste';
    ikSign:
      Result := 'Unterschrift';
    ikComment:
      Result := 'Notiz';
  end;
end;

function StrToReportItemKind(Value: string): TReportItemKind;
begin
  if Value = 'Überschrift' then
    Result := ikHeading
  else if Value = 'Absatz' then
    Result := ikParagraph
  else if Value = 'Bild' then
    Result := ikImage
  else if Value = 'Artikelliste' then
    Result := ikArtList
  else if Value = 'Unterschrift' then
    Result := ikSign
  else if Value = 'Notiz' then
    Result := ikComment
  else
    Result := ikUnknown;
end;

{ TWebReportItemList }

procedure TReportItemList.Add(Item: TReportItem);
var
  Index: Integer;
begin
  index := Self.IndexOf(Item);
  if index = -1 then
  begin
    Item.OnPropertyChanged := Self.HandlePropertyChanged;
    inherited Add(Item);
    Self.DoListChanged;
    Self.Signed := Self.IsSigned;
  end
  else
    Self.DoListError('Doppelter Eintrag: Datensatz ist schon vorhanden!');
end;

constructor TReportItemList.Create(Key: string);
var
  s: string;
begin
  Self.FSelectedItem := nil;
  FCreating := true;
  inherited Create;
  FKey := Key;
  Self.OnListChanged := nil;
  Self.OnListError := nil;
  Self.OnPropertyChanged := nil;
  s := TLocalStorage.GetValue(Self.Key);
  if s <> '' then
    Self.SetValues(s);
  FCreating := false;
end;

procedure TReportItemList.CreateHtml;
begin
  FTimer := TTimer.Create(nil);
  FTimer.Interval := 200;
  FTimer.OnTimer := Self.TimerOnTime;
  Fi := 0;
  FLst := TStringList.Create;
  FLst.Add(Self.HtmlHeader);
  FTimer.Enabled := true;
end;

procedure TReportItemList.Delete(Index: Integer);
begin
  inherited Delete(Index);
  Self.DoListChanged;
end;

procedure TReportItemList.DoHtmlReady(AHtml: string);
begin
  if Assigned(Self.OnHtmlReady) then
    Self.OnHtmlReady(Self, AHtml);
end;

procedure TReportItemList.DoListChanged;
begin
  if not FCreating then
    TLocalStorage.SetValue(Self.Key, Self.ToJson);
  if Assigned(Self.OnListChanged) then
    Self.OnListChanged(Self);
end;

procedure TReportItemList.DoListError(AMessage: string);
begin
  if Assigned(Self.OnListError) then
    Self.OnListError(Self, AMessage);
end;

procedure TReportItemList.DoPropertyChanged(PropertyName: string);
begin
  if Assigned(Self.OnPropertyChanged) then
    Self.FOnPropertyChanged(Self, PropertyName);
end;

function TReportItemList.GetItem(i: Integer): TReportItem;
begin
  Result := inherited GetItem(i) as TReportItem;
end;

procedure TReportItemList.HandlePropertyChanged(Sender: TObject;
  PropertyName: string);
begin
  Self.DoListChanged;
end;

function TReportItemList.HtmlFooter: string;
var
  lst: TStringList;
begin
  lst := TStringList.Create;
  lst.Add('		</div>');
  lst.Add('	</body>');
  lst.Add('</html>');
  Result := lst.Text;
  lst.Free;
end;

function TReportItemList.HtmlHeader: string;
var
  lst: TStringList;
begin
  lst := TStringList.Create;
  lst.Add('<!DOCTYPE html>');
  lst.Add('<html>');
  lst.Add('	<head>');
  lst.Add('		<title>WzWebPortal</title>');
  lst.Add('		<meta name="viewport" content="width=device-width, initial-scale=1" />');
  lst.Add('		<style>');
  lst.Add('			body {');
  lst.Add('				padding: 10px;');
  lst.Add('				}');
  lst.Add('		</style>');
  lst.Add('	</head>');
  lst.Add('	<body>');
  lst.Add('		<div id="maindiv" style="margin:10px">');
  Result := lst.Text;
  lst.Free;
end;

function TReportItemList.IndexOf(Item: TReportItem): Integer;
var
  i: Integer;
  found: Boolean;
begin
  if Item <> nil then
  begin
    i := 0;
    found := false;
    while not found and (i < Self.Count) do
      if Self.Items[i].Equals(Item) then
        found := true
      else
        Inc(i);
    if found then
      Result := i
    else
      Result := -1;
  end
  else
  begin
    Self.DoListError('Item ist nil!');
    Result := -2;
  end;
end;

procedure TReportItemList.Insert(Index: Integer; Item: TReportItem);
var
  i: Integer;
begin
  i := Self.IndexOf(Item);
  if i = -1 then
  begin
    Item.OnPropertyChanged := Self.HandlePropertyChanged;
    inherited Insert(Index, Item);
    Self.DoListChanged;
    Self.Signed := Self.IsSigned;
  end
  else
    Self.DoListError('Doppelter Eintrag: Datensatz ist schon vorhanden!');
end;

function TReportItemList.IsSigned: Boolean;
var
  i: Integer;
begin
  Result := false;
  i := 0;
  while not Result and (i < Self.Count) do
    if Self.Items[i].Kind = ikSign then
      Result := true
    else
      Inc(i);
end;

procedure TReportItemList.Move(FromIndex, ToIndex: Integer);
begin
  Self.Exchange(FromIndex, ToIndex);
  Self.DoListChanged;
end;

procedure TReportItemList.Reset;
begin
  Self.Clear;
  Self.DoListChanged;
end;

procedure TReportItemList.SetOnListChanged(const Value: TListChangedEvent);
begin
  FOnListChanged := Value;
end;

procedure TReportItemList.SetOnListError(const Value: TListErrorEvent);
begin
  FOnListError := Value;
end;

procedure TReportItemList.SetOnPropertyChanged
  (const Value: TPropertyChangedEvent);
begin
  FOnPropertyChanged := Value;
end;

procedure TReportItemList.SetSelectedItem(const Value: TReportItem);
begin
  if FSelectedItem <> Value then
  begin
    FSelectedItem := Value;
    Self.DoPropertyChanged('SelectedItem');
  end;
end;

procedure TReportItemList.SetSigned(const Value: Boolean);
begin
  if FSigned <> Value then
  begin
    FSigned := Value;
    Self.DoPropertyChanged('Signed');
  end;
end;

procedure TReportItemList.SetOnHtmlReady(const Value: THtmlReadyEvent);
begin
  FOnHtmlReady := Value;
end;

procedure TReportItemList.SetValues(Obj: TJsObject);
var
  Count, i: Integer;
  dummy: TListChangedEvent;
  Item: TReportItem;
begin
  dummy := Self.OnListChanged;
  Self.OnListChanged := nil;
  Self.Clear;
  if Obj['Count'] <> nil then
  begin
    Count := StrToInt(string(Obj['Count']));
    for i := 0 to Count - 1 do
      if Obj['Item' + IntToStr(i)] <> nil then
      begin
        Item := TReportItem.Create(string(Obj['Item' + IntToStr(i)]));
        Item.OnPropertyChanged := Self.HandlePropertyChanged;
        Self.Add(Item);
      end;
  end;
  Self.OnListChanged := dummy;
  Self.DoListChanged;
end;

procedure TReportItemList.SetValues(Json: string);
var
  Obj: TJsObject;
begin
  Obj := TJsJson.parseObject(Json);
  Self.SetValues(Obj);
end;

procedure TReportItemList.TimerOnTime(Sender: TObject);
var
  Item: TReportItem;
  procedure HandleFileOpen(Sender: TObject; ABase64: string);
  var
    s: string;
  begin
    with Sender as TReportImage do
    begin
      s := FormatBase64(ABase64, false);
      FLst.Add(s);
      Inc(Fi);
      FTimer.Enabled := true;
    end;
  end;
  procedure HandleOpenFile1(Sender: TObject; Text: string);
  var
    s: string;
  begin
    with Sender as TReportComment do
    begin
      s := FormatHtml(Text);
      FLst.Add(s);
      Inc(Fi);
      FTimer.Enabled := true;
    end;
  end;

begin
  FTimer.Enabled := false;
  if Fi < Self.Count then
  begin
    Item := Self.Items[Fi];
    case Item.Kind of
      ikUnknown:
        ;
      ikHeading, ikParagraph, ikArtList:
        begin
          FLst.Add(Item.SubItem.GetHtml);
          Inc(Fi);
          FTimer.Enabled := true;
        end;
      ikImage, ikSign:
        begin
          with Item.SubItem as TReportImage do
          begin
            OnFileOpen := HandleFileOpen;
            OpenFile;
          end;
        end;
      ikComment:
        begin
          with Item.SubItem as TReportComment do
          begin
            OnFileOpen := HandleOpenFile1;
            OpenFile;
          end;
        end;
    end;
  end
  else
  begin
    FLst.Add(Self.HtmlFooter);
    Self.DoHtmlReady(FLst.Text);
  end;
end;

function TReportItemList.ToJson: string;
var
  Obj: TJsonObject;
  i: Integer;
begin
  Obj := TJsonObject.Create;
  Obj.AddPair('Count', IntToStr(Self.Count));
  for i := 0 to Self.Count - 1 do
    Obj.AddPair('Item' + IntToStr(i), Self.Items[i].ToJson);
  Result := Obj.ToJson;
  Obj.Free;
end;

{ TReportBase }

constructor TReportBase.Create;
begin
  Self.OnPropertyChanged := nil;
  Self.ClearValues;
end;

procedure TReportBase.ClearValues;
begin
  //
end;

constructor TReportBase.Create(Json: string);
begin
  Self.Create;
  Self.SetValues(Json);
end;

procedure TReportBase.DoPropertyChanged(PropertyName: string);
begin
  if Assigned(Self.OnPropertyChanged) then
    Self.OnPropertyChanged(Self, PropertyName);
end;

function TReportBase.GetContent: string;
begin
  Result := '';
end;

function TReportBase.GetHtml: string;
begin
  Result := '';
end;

procedure TReportBase.SetOnPropertyChanged(const Value: TPropertyChangedEvent);
begin
  FOnPropertyChanged := Value;
end;

procedure TReportBase.SetValues(Json: string);
begin
  //
end;

function TReportBase.ToJson: string;
begin
  Result := '{}';
end;

{ TReportHeading }

procedure TReportHeading.ClearValues;
begin
  Self.Size := '';
  Self.Align := '';
  Self.Content := '';
end;

function TReportHeading.GetContent: string;
begin
  Result := Self.Content;
end;

procedure TReportHeading.SetAlign(const Value: string);
begin
  if FAlign <> Value then
  begin
    FAlign := Value;
    Self.DoPropertyChanged('Align');
  end;
end;

procedure TReportHeading.SetContent(const Value: string);
begin
  if FContent <> Value then
  begin
    FContent := Value;
    Self.DoPropertyChanged('Content');
  end;
end;

procedure TReportHeading.SetSize(const Value: string);
begin
  if FSize <> Value then
  begin
    FSize := Value;
    Self.DoPropertyChanged('Size');
  end;
end;

procedure TReportHeading.SetValues(Json: string);
var
  Obj: TJsObject;
begin
  Obj := TJsJson.parseObject(Json);
  Self.ClearValues;
  if Obj['Size'] <> nil then
    Self.Size := string(Obj['Size']);
  if Obj['Align'] <> nil then
    Self.Align := string(Obj['Align']);
  if Obj['Content'] <> nil then
    Self.Content := string(Obj['Content']);
end;

function TReportHeading.GetHtml: string;
var
  tag: string;
begin
  tag := 'h' + Self.Size;
  Result := '<' + tag;
  if Self.Align = 'links' then
    Result := Result + '>'
  else
  begin
    Result := Result + ' style="text-align:';
    if Self.Align = 'zentriert' then
      Result := Result + 'center">'
    else
      Result := Result + 'right">';
  end;
  Result := Result + Self.Content + '</' + tag + '>';
end;

function TReportHeading.ToJson: string;
var
  Obj: TJsonObject;
begin
  Obj := TJsonObject.Create;
  Obj.AddPair('Size', Self.Size);
  Obj.AddPair('Align', Self.Align);
  Obj.AddPair('Content', Self.Content);
  Result := Obj.ToJson;
  Obj.Free;
end;

{ TReportParagraph }

procedure TReportParagraph.ClearValues;
begin
  if Self.Content = nil then
  begin
    Self.Content := TStringList.Create;
    Self.Content.OnChange := Self.HandleChanged;
  end
  else
    Self.Content.Clear;
end;

function TReportParagraph.GetContent: string;
begin
  if (Self.Content <> nil) and (Self.Content.Count > 0) then
    Result := Self.Content[0]
  else
    Result := '';
end;

function TReportParagraph.GetHtml: string;
var
  i: Integer;
  s: string;
begin
  Result := '<p>';
  if Self.Content <> nil then
  begin
    s := '';
    for i := 0 to Self.Content.Count - 1 do
      s := s + Self.Content[i] + '<br>';
    if s <> '' then
      Delete(s, Length(s) - 1, 4);
  end;
  Result := Result + s + '</p>';
end;

procedure TReportParagraph.HandleChanged(Sender: TObject);
begin
  Self.DoPropertyChanged('Content');
end;

procedure TReportParagraph.SetContent(const Value: TStringList);
begin
  if FContent <> Value then
  begin
    FContent := Value;
    Self.DoPropertyChanged('Content');
  end;
end;

procedure TReportParagraph.SetValues(Json: string);
var
  Obj: TJsObject;
begin
  Obj := TJsJson.parseObject(Json);
  Self.ClearValues;
  if Obj['Content'] <> nil then
  begin
    if Self.Content = nil then
      Self.Content := TStringList.Create
    else
      Self.Content.Clear;
    Self.Content.Text := string(Obj['Content']);
  end;
end;

function TReportParagraph.ToJson: string;
var
  Obj: TJsonObject;
begin
  if (Self.Content <> nil) and (Self.Content.Count > 0) then
  begin
    Obj := TJsonObject.Create;
    Obj.AddPair('Content', Self.Content.Text);
    Result := Obj.ToJson;
    Obj.Free;
  end
  else
    Result := '{}';
end;

{ TReportImage }

procedure TReportImage.ClearValues;
begin
  Self.Filename := '';
  Self.Legend := '';
  Self.Pathname := '';
end;

constructor TReportImage.Create;
begin
  inherited Create;
  Self.OnFileSave := Self.HandleFileSave;
end;

constructor TReportImage.Create(Json: string);
begin
  inherited Create(Json);
  Self.OnFileSave := Self.HandleFileSave;
end;

procedure TReportImage.DoFileOpen(ABase64: string);
begin
  if Assigned(Self.OnFileOpen) then
    Self.OnFileOpen(Self, ABase64);
end;

procedure TReportImage.DoFileSave;
begin
  if Assigned(Self.OnFileSave) then
    Self.OnFileSave(Self);
end;

function TReportImage.FormatBase64(Value: string; Small: Boolean): string;
begin
  if Small then
    Result := '<img src="' + Value + '" style="width:32px;height:32px" />'
  else
  begin
    Result := '';
    Result := Result + '<div><img src="' + Value +
      '" style="width:300px;object-fit:cover" />';
    if Self.Legend <> '' then
      Result := Result + '<p>' + Self.Legend + '</p></div>';
  end;
end;

procedure TReportImage.HandleFileSave(Sender: TObject);
begin
  if Assigned(Self.OnPropertyChanged) then
    Self.OnPropertyChanged(Self, 'Base64');
end;

procedure TReportImage.OpenFile;
var
  tFile: TTextFile;
  procedure HandleFileOpen(Sender: TObject);
  begin
    Self.DoFileOpen(tFile.Text)
  end;

  procedure HandleFileExists(Sender: TObject; AExists: Boolean);
  begin
    if AExists then
      tFile.OpenFile(Self.Pathname, Self.Filename);
  end;

begin
  tFile := TTextFile.Create(nil);
  tFile.OnFileOpen := HandleFileOpen;
  tFile.OnFileExists := HandleFileExists;
  tFile.FileExists(Self.Pathname, Self.Filename);
end;

procedure TReportImage.SaveFile(ABase64: string);
var
  tFile: TTextFile;
  procedure HandleFileSaveIntern(Sender: TObject);
  begin
    Self.DoFileSave;
  end;

begin
  tFile := TTextFile.Create(nil);
  tFile.OnFileSave := HandleFileSaveIntern;
  tFile.Text := ABase64;
  tFile.SaveAsFile(Self.Pathname, Self.Filename);
end;

procedure TReportImage.SetCol(const Value: Integer);
begin
  FCol := Value;
end;

procedure TReportImage.SetFilename(const Value: string);
begin
  if FFilename <> Value then
  begin
    FFilename := Value;
    Self.DoPropertyChanged('Filename');
  end;
end;

procedure TReportImage.SetLegend(const Value: string);
begin
  if FLegend <> Value then
  begin
    FLegend := Value;
    Self.DoPropertyChanged('Legend');
  end;
end;

procedure TReportImage.SetOnFileOpen(const Value: TFileOpenEvent);
begin
  FOnFileOpen := Value;
end;

procedure TReportImage.SetOnFileSave(const Value: TFileSaveEvent);
begin
  FOnFileSave := Value;
end;

procedure TReportImage.SetPathname(const Value: string);
begin
  if FPathname <> Value then
  begin
    FPathname := Value;
    Self.DoPropertyChanged('Pathname');
  end;
end;

procedure TReportImage.SetRow(const Value: Integer);
begin
  FRow := Value;
end;

procedure TReportImage.SetValues(Json: string);
var
  Obj: TJsObject;
begin
  Obj := TJsJson.parseObject(Json);
  Self.ClearValues;
  if Obj['Filename'] <> nil then
    Self.Filename := string(Obj['Filename']);
  if Obj['Legend'] <> nil then
    Self.Legend := string(Obj['Legend']);
  if Obj['Pathname'] <> nil then
    Self.Pathname := string(Obj['Pathname']);
end;

function TReportImage.ToJson: string;
var
  Obj: TJsonObject;
begin
  Obj := TJsonObject.Create;
  Obj.AddPair('Filename', Self.Filename);
  Obj.AddPair('Legend', Self.Legend);
  Obj.AddPair('Pathname', Self.Pathname);
  Result := Obj.ToJson;
  Obj.Free;
end;

{ TReportArtItem }

procedure TReportArtItem.ClearValues;
begin
  Self.ArtNr := '';
  Self.Menge := 0;
  Self.Bezeichnung := '';
  Self.Einheit := '';
end;

function TReportArtItem.GetHtml: string;
begin
  Result := '<tr><td>' + Self.ArtNr + '</td><td style="text-align:right">' +
    Format('%.2n', [Self.Menge]) + '</td><td>' + Self.Einheit + '</td><td>' +
    Self.Bezeichnung + '</td ></tr>';
end;

procedure TReportArtItem.SetArtNr(const Value: string);
begin
  if FArtNr <> Value then
  begin
    FArtNr := Value;
    Self.DoPropertyChanged('ArtNr');
  end;
end;

procedure TReportArtItem.SetBezeichnung(const Value: string);
begin
  if FBezeichnung <> Value then
  begin
    FBezeichnung := Value;
    Self.DoPropertyChanged('Bezeichnung');
  end;
end;

procedure TReportArtItem.SetEinheit(const Value: string);
begin
  if FEinheit <> Value then
  begin
    FEinheit := Value;
    Self.DoPropertyChanged('Einheit');
  end;
end;

procedure TReportArtItem.SetMenge(const Value: Double);
begin
  if FMenge <> Value then
  begin
    FMenge := Value;
    Self.DoPropertyChanged('Menge');
  end;
end;

procedure TReportArtItem.SetValues(Json: string);
var
  Obj: TJsObject;
begin
  Obj := TJsJson.parseObject(Json);
  Self.ClearValues;
  if Obj['ArtNr'] <> nil then
    Self.ArtNr := string(Obj['ArtNr']);
  if Obj['Menge'] <> nil then
    Self.Menge := StrToFloat(string(Obj['Menge']));
  if Obj['Bezeichnung'] <> nil then
    Self.Bezeichnung := string(Obj['Bezeichnung']);
  if Obj['Einheit'] <> nil then
    Self.Einheit := string(Obj['Einheit']);
end;

function TReportArtItem.ToJson: string;
var
  Obj: TJsonObject;
begin
  Obj := TJsonObject.Create;
  Obj.AddPair('ArtNr', Self.ArtNr);
  Obj.AddPair('Menge', FloatToStr(Self.Menge));
  Obj.AddPair('Bezeichnung', Self.Bezeichnung);
  Obj.AddPair('Einheit', Self.Einheit);
  Result := Obj.ToJson;
  Obj.Free;
end;

{ TReportArtList }

procedure TReportArtList.Add(Item: TReportArtItem);
var
  c: Integer;
begin
  c := Self.Count;
  Inc(c);
  Self.Count := c;
  FItems[c - 1] := Item;
end;

constructor TReportArtList.Create;
begin
  FCount := 0;
end;

constructor TReportArtList.Create(Json: string);
begin
  Self.SetValues(Json);
end;

procedure TReportArtList.Delete(Index: Integer);
var
  i: Integer;
begin
  if (0 <= Index) and (Index < Self.Count) then
  begin
    FItems[Index].Free;
    for i := Index to Count - 2 do
      FItems[i] := FItems[i + 1];
    Self.Count := Self.Count - 1;
    Self.DoPropertyChanged('Items');
  end;
end;

procedure TReportArtList.DoPropertyChanged(PropertyName: string);
begin
  if Assigned(Self.OnPropertyChanged) then
    Self.OnPropertyChanged(Self, PropertyName);
end;

function TReportArtList.GetContent: string;
begin
  Result := 'Anzahl Artikel: ' + IntToStr(Self.Count);
end;

function TReportArtList.GetHtml: string;
var
  i: Integer;
begin
  Result := '<table><tr><th>Artikel-Nr.</th><th>Menge</th><th>Einheit</th>' +
    '<th>Bezeichnung</th></tr>';
  for i := 0 to Self.Count - 1 do
    Result := Result + Self.GetItem(i).GetHtml;
  Result := Result + '</table>';
end;

function TReportArtList.GetItem(i: Integer): TReportArtItem;
begin
  Result := FItems[i];
end;

procedure TReportArtList.HandlePropertyChanged(Sender: TObject;
  PropertyName: string);
begin
  Self.DoPropertyChanged(PropertyName);
end;

procedure TReportArtList.SetCount(const Value: Integer);
var
  i: Integer;
begin
  if FCount < Value then
  begin
    SetLength(FItems, Value);
    for i := FCount to Value - 1 do
    begin
      FItems[i] := TReportArtItem.Create;
      FItems[i].OnPropertyChanged := Self.HandlePropertyChanged;
    end;
    FCount := Value;
  end
  else if FCount > Value then
  begin
    for i := Value to FCount - 1 do
    begin
      FItems[i].OnPropertyChanged := nil;
      FItems[i].Free;
    end;
    SetLength(FItems, Value);
    FCount := Value;
  end;
end;

procedure TReportArtList.SetValues(Json: string);
var
  Obj: TJsObject;
  i, Count: Integer;
begin
  Self.Count := 0;
  Obj := TJsJson.parseObject(Json);
  if Obj['Count'] <> nil then
  begin
    Count := StrToInt(string(Obj['Count']));
    for i := 0 to Count - 1 do
      if Obj['Item' + IntToStr(i)] <> nil then
        Self.Add(TReportArtItem.Create(string(Obj['Item' + IntToStr(i)])));
  end;
end;

function TReportArtList.ToJson: string;
var
  Obj: TJsonObject;
  i: Integer;
begin
  Obj := TJsonObject.Create;
  Obj.AddPair('Count', IntToStr(Self.Count));
  for i := 0 to Self.Count - 1 do
    Obj.AddPair('Item' + IntToStr(i), Self.GetItem(i).ToJson);
  Result := Obj.ToJson;
  Obj.Free;
end;

{ TWebReport }

procedure TReport.CheckPdfFile;
var
  tFile: TTextFile;
  procedure HandleFileExists(Sender: TObject; AValue: Boolean);
  begin
    // AValue:=false;
    Self.PdfFileExists := AValue;
  end;

begin
  if (Self.PdfPathname <> '') and (Self.PdfFilename <> '') then
  begin
    tFile := TTextFile.Create(nil);
    tFile.OnFileExists := HandleFileExists;
    tFile.FileExists(Self.PdfPathname, Self.PdfFilename);
  end;
end;

procedure TReport.ClearValues;
begin
  Self.Name := '';
  Self.Datum := 0;
  Self.User := nil;
  Self.Fak := nil;
  Self.ReportItemList := nil;
  Self.PdfPathname := '';
  Self.PdfFilename := '';
  Self.CreatedAt := now;
  Self.TransmittedAt := 0;
  Self.FPdfFileExists := false;
end;

constructor TReport.Create;
begin
  Self.OnPropertyChanged := nil;
  Self.ClearValues;
end;

constructor TReport.Create(Json: string);
begin
  Self.OnPropertyChanged := nil;
  Self.SetValues(Json);
end;

procedure TReport.DoPropertyChanged(PropertyName: string);
begin
  if Assigned(Self.OnPropertyChanged) then
    Self.OnPropertyChanged(Self, PropertyName);
end;

function TReport.Equals(Other: TReport): Boolean;
begin
  if Other <> nil then
    Result := Self.Name = Other.Name
  else
    Result := false;
end;

procedure TReport.HandlePropertyChanged(Sender: TObject;
  PropertyName: string);
begin
  Self.DoPropertyChanged(PropertyName);
end;

procedure TReport.SetCreatedAt(const Value: TDateTime);
begin
  if FCreatedAt <> Value then
  begin
    FCreatedAt := Value;
    Self.DoPropertyChanged('FCreatedAt')
  end;
end;

procedure TReport.SetDatum(const Value: TDateTime);
begin
  if FDatum <> Value then
  begin
    FDatum := Value;
    Self.DoPropertyChanged('Datum');
  end;
end;

procedure TReport.SetFak(const Value: TFakModel);
begin
  if FFak <> Value then
  begin
    FFak := Value;
    Self.DoPropertyChanged('Fak');
  end;
end;

procedure TReport.SetName(const Value: string);
begin
  if FName <> Value then
  begin
    FName := Value;
    Self.ReportItemList := TReportItemList.Create(Self.Name);
    Self.DoPropertyChanged('Name');
  end;
end;

procedure TReport.SetOnPropertyChanged(const Value: TPropertyChangedEvent);
begin
  FOnPropertyChanged := Value;
end;

procedure TReport.SetPdfFileExists(const Value: Boolean);
begin
  if FPdfFileExists <> Value then
  begin
    FPdfFileExists := Value;
    Self.DoPropertyChanged('PdfFileExists');
  end;
end;

procedure TReport.SetPdfFilename(const Value: string);
begin
  if FPdfFilename <> Value then
  begin
    FPdfFilename := Value;
    Self.DoPropertyChanged('PdfFilename');
    Self.CheckPdfFile;
  end;
end;

procedure TReport.SetPdfPathname(const Value: string);
begin
  if FPdfPathname <> Value then
  begin
    FPdfPathname := Value;
    Self.DoPropertyChanged('PdfPathname');
    Self.CheckPdfFile;
  end;
end;

procedure TReport.SetReportItemList(const Value: TReportItemList);
begin
  FReportItemList := Value;
end;

procedure TReport.SetTransmittedAt(const Value: TDateTime);
begin
  if FTransmittedAt <> Value then
  begin
    FTransmittedAt := Value;
    Self.DoPropertyChanged('TransmittedAt');
  end;
end;

procedure TReport.SetUser(const Value: TUserModel);
begin
  if FUser <> Value then
  begin
    FUser := Value;
    Self.DoPropertyChanged('User');
  end;
end;

procedure TReport.SetValues(Json: string);
var
  Obj: TJsObject;
begin
  Obj := TJsJson.parseObject(Json);
  Self.ClearValues;
  if Obj['Name'] <> nil then
    Self.Name := string(Obj['Name']);
  if Obj['Datum'] <> nil then
    Self.Datum := StrToDateTime(string(Obj['Datum']));
  if Obj['Fak'] <> nil then
    Self.Fak := TFakModel.Create(string(Obj['Fak']));
  if Obj['User'] <> nil then
    Self.User := TUserModel.Create(string(Obj['User']));
  if Obj['PdfPathname'] <> nil then
    Self.PdfPathname := string(Obj['PdfPathname']);
  if Obj['PdfFilename'] <> nil then
    Self.PdfFilename := string(Obj['PdfFilename']);
  if Obj['CreatedAt'] <> nil then
    Self.CreatedAt := StrToDateTime(string(Obj['CreatedAt']));
  if Obj['TransmittedAt'] <> nil then
    Self.TransmittedAt := StrToDateTime(string(Obj['TransmittedAt']));
end;

function TReport.ToJson: string;
var
  Obj: TJsonObject;
begin
  Obj := TJsonObject.Create;
  Obj.AddPair('Name', Self.Name);
  Obj.AddPair('Datum', DateTimeToStr(Self.Datum));
  if Self.Fak <> nil then
    Obj.AddPair('Fak', Self.Fak.ToJson);
  if Self.User <> nil then
    Obj.AddPair('User', Self.User.ToJson);
  Obj.AddPair('PdfPathname', Self.PdfPathname);
  Obj.AddPair('PdfFilename', Self.PdfFilename);
  Obj.AddPair('CreatedAt', DateTimeToStr(Self.CreatedAt));
  Obj.AddPair('TransmittedAt', DateTimeToStr(Self.TransmittedAt));
  Result := Obj.ToJson;
  Obj.Free;
end;

function TReport.ToString: string;
begin
  Result := 'Bericht vom ' + FormatDateTime('dd.mm.yyyy', Self.Datum) + #13#10;
  Result := Result + 'Author: ' + Self.User.ToString + #13#10;
  Result := Result + 'Auftrag: ' + Self.Fak.ToStringV1;
end;

{ TReportComment }

procedure TReportComment.ClearValues;
begin
  Self.Filename := '';
  Self.Pathname := '';
end;

procedure TReportComment.DoFileOpen(Text: string);
begin
  if Assigned(Self.OnFileOpen) then
    Self.OnFileOpen(Self, Text);
end;

function TReportComment.FormatHtml(Content: string): string;
var
  i: Integer;
  s: string;
  lst: TStringList;
begin
  lst := TStringList.Create;
  lst.Text := Content;
  Result := '<p>';
  s := '';
  for i := 0 to lst.Count - 1 do
    s := s + lst[i] + '<br>';
  if s <> '' then
    Delete(s, Length(s) - 1, 4);
  Result := Result + s + '</p>';
end;

procedure TReportComment.OpenFile;
var
  tFile: TTextFile;
  procedure HandleFileOpen(Sender: TObject);
  begin
    Self.DoFileOpen(tFile.Text)
  end;

  procedure HandleFileExists(Sender: TObject; AExists: Boolean);
  begin
    if AExists then
      tFile.OpenFile(Self.Pathname, Self.Filename);
  end;

begin
  tFile := TTextFile.Create(nil);
  tFile.OnFileOpen := HandleFileOpen;
  tFile.OnFileExists := HandleFileExists;
  tFile.FileExists(Self.Pathname, Self.Filename);
end;

procedure TReportComment.SetCol(const Value: Integer);
begin
  FCol := Value;
end;

procedure TReportComment.SetFilename(const Value: string);
begin
  FFilename := Value;
end;

procedure TReportComment.SetOnFileOpen(const Value: TFileOpenEvent);
begin
  FOnFileOpen := Value;
end;

procedure TReportComment.SetPathname(const Value: string);
begin
  FPathname := Value;
end;

procedure TReportComment.SetRow(const Value: Integer);
begin
  FRow := Value;
end;

procedure TReportComment.SetValues(Json: string);
var
  Obj: TJsObject;
begin
  Obj := TJsJson.parseObject(Json);
  Self.ClearValues;
  if Obj['Filename'] <> nil then
    Self.Filename := string(Obj['Filename']);
  if Obj['Pathname'] <> nil then
    Self.Pathname := string(Obj['Pathname']);
end;

function TReportComment.ToJson: string;
var
  Obj: TJsonObject;
begin
  Obj := TJsonObject.Create;
  Obj.AddPair('Filename', Self.Filename);
  Obj.AddPair('Pathname', Self.Pathname);
  Result := Obj.ToJson;
  Obj.Free;
end;

{ TWebReportList }

procedure TReportList.Add(Item: TReport);
begin
  if not Self.Exists(Item) then
  begin
    Item.OnPropertyChanged := Self.HandlePropertyChanged;
    inherited Add(Item);
    Self.DoListChanged;
  end
  else
    Self.DoListError('Doppelter Report: ' + Item.Name);
end;

constructor TReportList.Create;
begin
  inherited Create;
  FKey := 'ReportList_';
  Self.OnListChanged := nil;
  Self.OnListError := nil;
  Self.Fak := nil;
  Self.SelectedItem := nil;
  Self.PersonalNr := '';
end;

procedure TReportList.Delete(i: Integer);
begin
  if (0 <= i) and (i < Self.Count) then
  begin
    inherited Delete(i);
    Self.DoListChanged;
    Self.SelectedItem := nil;
  end;
end;

procedure TReportList.DoListChanged;
begin
  if Assigned(Self.OnListChanged) then
  begin
    Self.Store;
    Self.OnListChanged(Self);
  end;
end;

procedure TReportList.DoListError(AMsg: string);
begin
  if Assigned(Self.OnListError) then
    Self.OnListError(Self, AMsg);
end;

procedure TReportList.DoPropertyChanged(PropertyName: string);
begin
  if Assigned(Self.OnPropertyChanged) then
    Self.OnPropertyChanged(Self, PropertyName);
end;

function TReportList.Exists(Item: TReport): Boolean;
begin
  Result := Self.IndexOf(Item) >= 0;
end;

function TReportList.GetByPathAndFilename(APathname, AFilename: string)
  : TReport;
var
  i: Integer;
  found: Boolean;
  Item: TReport;
begin
  found := false;
  i := 0;
  while not found and (i < Self.Count) do
  begin
    Item := Self.Items[i];
    if (Item.PdfPathname = APathname) and (Item.PdfFilename = AFilename) then
      found := true
    else
      Inc(i);
  end;
  if found then
    Result := Item
  else
    Result := nil;
end;

function TReportList.GetItem(i: Integer): TReport;
begin
  Result := inherited GetItem(i) as TReport;
end;

procedure TReportList.HandlePropertyChanged(Sender: TObject;
  PropertyName: string);
begin
  if (PropertyName = 'TransmittedAt') or (PropertyName = 'Datum') or
    (PropertyName = 'PdfFileExists') then
    Self.DoListChanged;
end;

function TReportList.IndexOf(Item: TReport): Integer;
var
  i: Integer;
  found: Boolean;
begin
  found := false;
  i := 0;
  while not found and (i < Self.Count) do
    if Self.Items[i].Equals(Item) then
      found := true
    else
      Inc(i);
  if found then
    Result := i
  else
    Result := -1;
end;

procedure TReportList.Load;
var
  s: string;
begin
  if (Self.Fak <> nil) and (Self.Fak.Nr <> '') and (Self.PersonalNr <> '') then
  begin
    s := TLocalStorage.GetValue(FKey + Self.PersonalNr + '_' + Self.Fak.Nr);
    if s <> '' then
      Self.SetValues(s)
    else
    begin
      Self.Clear;
      Self.DoListChanged;
    end;
    Self.SelectedItem := nil;
  end;
end;

procedure TReportList.SetFak(const Value: TFakModel);
begin
  FFak := Value;
end;

procedure TReportList.SetOnListChanged(const Value: TListChangedEvent);
begin
  FOnListChanged := Value;
end;

procedure TReportList.SetOnListError(const Value: TListErrorEvent);
begin
  FOnListError := Value;
end;

procedure TReportList.SetOnPropertyChanged(const Value
  : TPropertyChangedEvent);
begin
  FOnPropertyChanged := Value;
end;

procedure TReportList.SetPersonalNr(const Value: string);
begin
  FPersonalNr := Value;
end;

procedure TReportList.SetSelectedItem(const Value: TReport);
begin
  FSelectedItem := Value;
  Self.DoPropertyChanged('SelectedItem');
end;

procedure TReportList.SetValues(Json: string);
var
  Obj: TJsObject;
  i, Count: Integer;
  Item: TReport;
  dummy: TListChangedEvent;
begin
  dummy := Self.OnListChanged;
  Self.OnListChanged := nil;
  Obj := TJsJson.parseObject(Json);
  Self.Clear;
  if Obj['Count'] <> nil then
  begin
    Count := StrToInt(string(Obj['Count']));
    for i := 0 to Count - 1 do
      if Obj['Item' + IntToStr(i)] <> nil then
      begin
        Item := TReport.Create(string(Obj['Item' + IntToStr(i)]));
        Item.OnPropertyChanged := Self.HandlePropertyChanged;
        Self.Add(Item);
      end;
  end;
  Self.OnListChanged := dummy;
  Self.DoListChanged;
end;

procedure TReportList.Store;
begin
  if (Self.Fak <> nil) and (Self.Fak.Nr <> '') and (Self.PersonalNr <> '') then
    TLocalStorage.SetValue(FKey + Self.PersonalNr + '_' + Self.Fak.Nr,
      Self.ToJson);
end;

function TReportList.ToJson: string;
var
  Obj: TJsonObject;
  i: Integer;
begin
  Obj := TJsonObject.Create;
  Obj.AddPair('Count', IntToStr(Self.Count));
  for i := 0 to Self.Count - 1 do
    Obj.AddPair('Item' + IntToStr(i), Self.Items[i].ToJson);
  Result := Obj.ToJson;
  Obj.Free;
end;

end.
